import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useForm } from "react-hook-form";

import WSTextField from "../../../Components/TextBox/WSTextBox";
import WSPhone from "../../../Components/Phone/WSPhone";
import { callAPI, Method } from "../../../commonFunctions/CommonFunctions";
import WSTextAreaField from "../../../Components/WSTextArea/WSTextArea";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ConfirmationPopup from "../../../Components/Confirmation/Confirmation";

const Customer = () => {
  const { register } = useForm();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const { showMessage } = useSnackbar();
  const [selectedId, setSelectedId] = useState<string | number>(0);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const params = useParams();
  const id = params.id;
  const navigate = useNavigate();

  const location = useLocation();
  const from = location.state?.from;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      CustomerName: "",
      Phone: "",
      altPhone: "",
      Email: "",
      GSTNumber: "",
      Address: "",
      IsActive: true,
    },
  });

  useEffect(() => {


    if (!id || id === "undefined" || id === "null" || id.trim() === "") {
      setIsEditMode(false);
      reset();
      return;
    }


    setIsEditMode(true);
    fetchCustomerData(id);
  }, [id, showMessage, reset]);

  const fetchCustomerData = async (CustomerId: string) => {
    try {
      setLoading(true);

      const response = await callAPI(`customers/${CustomerId}`, Method.GET);

      if (response?.success && response.data) {
        const customerData = response.data;

        reset({
          CustomerName: customerData.CustomerName || "",
          Phone: customerData.Phone || "",
          altPhone: customerData.altPhone || "",
          Email: customerData.Email || "",
          GSTNumber: customerData.GSTNumber || "",
          Address: customerData.Address || "",
          IsActive: customerData.IsActive !== false,
        });
      } else {
        console.error("❌ No data in response:", response);
        showMessage(response?.message || `Supplier ${CustomerId} not found`, "error");
        setIsEditMode(false);
      }
    } catch (error: any) {
      console.error("❌ Fetch error:", error.response?.data || error.message);
      showMessage("Failed to load supplier data", "error");
      setIsEditMode(false);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);

      const requestBody = {
        CustomerName: data.CustomerName?.trim(),
        Phone: data.Phone || "",
        altPhone: data.altPhone || "",
        Email: data.Email?.trim() || "",
        Address: data.Address?.trim() || "",
        GSTNumber: data.GSTNumber?.trim() || "",
        IsActive: data.IsActive || true,
      };

      let response;
      if (isEditMode && id) {
        response = await callAPI(`customers/${id}`, Method.PUT, requestBody);
      } else {
        response = await callAPI("customers/create", Method.POST, requestBody);
      }

      if (response?.success) {
        showMessage(
          isEditMode ? "customer updated successfully!" : "customer created successfully!",
          "success"
        );
        // navigate("/customer/list");
        if (response?.success) {
          if (from === "stockout") {
            navigate("/stockout");
          }
          else if (from === "quotation") {
            navigate("/quotation");
          }
          else if (from === "invoice") {
            navigate("/invoice");
          }

          else {
            navigate("/customer/list");
          }
        }
      } else {
        showMessage(response?.message || "Operation failed", "error");
      }
    } catch (error: any) {
      console.error("❌ Submit error:", error);
      showMessage(error.response?.data?.detail || "Operation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (!id) return;
    setSelectedId(id);
    setPopupOpen(true);
  };

  return (
    <Box sx={{ p: 2, background: "#f0f0f0", minHeight: "100vh" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>

          <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
            {isEditMode ? "Customer Management" : "Customer Management"}
          </Typography>
        </Box>
        <Box>
          {/* <IconButton sx={{ background: "#000", color: "#fff", mr: 1 }}>
            <EditIcon fontSize="small" />
          </IconButton> */}
          {isEditMode && id && (
            <IconButton
              onClick={handleDeleteClick}
              disabled={loading}
              sx={{
                background: "black",
                color: "#fff",
                "&:hover": { background: "black" },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* CARD */}
      <Paper
        sx={{
          borderRadius: "16px",
          p: 3,
          background: "#fafafa",
          position: "relative"
        }}
      >
        {loading && (
          <Box sx={{
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(255,255,255,0.9)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10, borderRadius: "16px"
          }}>
            <CircularProgress />
          </Box>
        )}

        {/* TITLE & SAVE BUTTON - OLD DESIGN */}
        <Box display="flex" justifyContent="space-between" mb={7}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
            {isEditMode ? "Customer Details" : "Customer Details"}
          </Typography>
          <Tooltip title={isEditMode ? "Update Customer" : "Create Customer"} arrow>
            <IconButton
              sx={{ background: "#000", color: "#fff" }}
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || loading}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>


        <Grid container rowSpacing={8} columnSpacing={5}>




          <Grid item xs={4}>
            <WSTextField
              name="CustomerName"
              label="Customer Name"
              id="CustomerName"
              control={control}
              rules={{required:"Customer name is required"}}
            />
          </Grid>

          {/* Row 2 */}

          <Grid item xs={4}>
            <WSPhone
              name="Phone"
              label="Phone Number"
              id="Phone" control={control} />
          </Grid>

          <Grid item xs={12} md={4}>
            <WSTextField
              id="Email"
              label="Email"
              {...register("Email")}
              control={control}
            />
          </Grid>
          <Grid item xs={4}>
            <WSTextField
              name="GSTNumber"
              label="TRN Number"
              id="GSTNumber"
              control={control}

            />
          </Grid>
          <Grid item xs={4}>
            <WSPhone
              name="altPhone"
              label="Alternate Phone Number"
              id="altPhone" control={control} />
          </Grid>

          <Grid item xs={6}>
            <WSTextAreaField
              name="Address"
              label="Address"
              id="Address"
              control={control}
            />
          </Grid>


        </Grid>
      </Paper>
      <ConfirmationPopup
        type="DELETE"
        apiUrl="customers"
        id={selectedId}
        confirmMessage="Are you sure you want to delete this customer?"
        open={isPopupOpen}
        onClose={() => setPopupOpen(false)}
        onConfirm={async (data) => {
          if (!selectedId) return;

          if (data?.success === true) {


            showMessage("customers deleted successfully!", "success");
            navigate("/customer/list");
          } else {
            showMessage(data?.detail, "error");
          }

          setPopupOpen(false);
        }}
      />
    </Box>
  );
};

export default Customer;